import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import OnboardingTable from 'features/Onboarding/components/OnboardingTable'
import IncompleteOnboardingBanner from 'features/Onboarding/components/IncompleteOnboardingBanner'
import styled from 'styled-components'
import WelcomeModal from 'features/Onboarding/components/WelcomeModal'
import { onboardingActions } from 'features/Onboarding/onboardingActions'
import { useDispatch } from 'react-redux'
import PayCadenceDrawer from './PayCadenceDrawer'
import KYCCompletionDrawer from './KYCCompletionDrawer'
import WorksiteSyncDrawer from './WorksiteSyncDrawer'
import AdditionalInformationDrawer from './AdditionalInformationDrawer'
import CommuterBenefitSetupDrawer from './CommuterBenefitSetupDrawer'
import { Locations } from 'shared/Router/Locations'
import { useNavigate } from 'react-router-dom'
import { OnboardingSection } from 'features/Onboarding/onboardingSlice'
import {
  EmployeeSyncRow,
  FundingSourceVerificationRow,
  WorksiteSyncRow,
  KYCCompletionRow,
  PayCadenceRow,
  CommuterBenefitSetupRow,
  ProgramLaunchRow,
  EmployeeInviteRow,
  AdditionalDetailsRow,
} from 'features/Onboarding/helperFunctions'
import { sortBy } from 'lodash-es'
import { hasAnyActivePrograms } from 'features/Onboarding/helperFunctions'

const Container = styled.div`
  padding: 2rem;
`

const AsyncItemsView = React.memo(() => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const onboarding = useSelector((state: AppState) => state.onboarding)
  const employer = useSelector((state: AppState) => state.employer.profile)
  const allFlexPrograms = useSelector(
    (state: AppState) => state.flex.flexPrograms
  )
  const allGrhPrograms = useSelector((state: AppState) => state.grh.grhPrograms)
  const allQtfPrograms = useSelector((state: AppState) => state.qtf.qtfPrograms)
  const allMicromobilityPrograms = useSelector(
    (state: AppState) => state.micromobility.micromobilityPrograms
  )
  const [drawers, setDrawers] = useState({
    payCadence: false,
    kycCompletion: false,
    worksiteSync: false,
    additionalInformation: false,
    commuterBenefitSetup: false,
  })

  // If there are any invited commuters, mark the EMPLOYEE_INVITE section as completed
  useEffect(() => {
    if (onboarding.hasInvitedCommuters) {
      dispatch(
        onboardingActions.updateOnboardingSectionStatus({
          sectionType: 'EMPLOYEE_INVITE',
          status: 'COMPLETED',
        })
      )
    }
  }, [onboarding.hasInvitedCommuters])

  // If all the sections are complete mark the onboarding stage to SUCCESS
  useEffect(() => {
    if (
      onboarding.sections.every((section) => section.status === 'COMPLETED')
    ) {
      dispatch(onboardingActions.updateOnboardingStage('SUCCESS'))
    }
  }, [onboarding.sections])

  // If there is at least one program, mark the PROGRAM_LAUNCH section as completed
  useEffect(() => {
    if (
      hasAnyActivePrograms(
        allGrhPrograms,
        allQtfPrograms,
        allMicromobilityPrograms,
        allFlexPrograms
      )
    ) {
      dispatch(
        onboardingActions.updateOnboardingSectionStatus({
          sectionType: 'PROGRAM_LAUNCH',
          status: 'COMPLETED',
        })
      )
    }
  }, [
    allGrhPrograms,
    allQtfPrograms,
    allMicromobilityPrograms,
    allFlexPrograms,
  ])

  const renderRow = (sections: OnboardingSection[]) => {
    const rows = []
    if (sections.length === 0) {
      return rows
    }
    // Sorting the rows by status
    const sortedSections = sortBy(sections, 'status')
    sortedSections.forEach((section) => {
      if (section.sectionType === 'WORKSITE_SYNC') {
        rows.push(
          WorksiteSyncRow(onboarding, employer.createdAt, () =>
            setDrawers({ ...drawers, worksiteSync: true })
          )
        )
      }
      if (section.sectionType === 'EMPLOYEE_SYNC') {
        rows.push(EmployeeSyncRow(onboarding, employer.createdAt))
      }
      if (section.sectionType === 'FUNDING_SOURCE_VERIFICATION') {
        rows.push(FundingSourceVerificationRow(onboarding, employer.createdAt))
      }
      if (section.sectionType === 'KYC_VERIFICATION') {
        rows.push(
          KYCCompletionRow(onboarding, employer.createdAt, () =>
            setDrawers({ ...drawers, kycCompletion: true })
          )
        )
      }
      if (section.sectionType === 'PAYROLL_DATES') {
        rows.push(
          PayCadenceRow(onboarding, employer.createdAt, () =>
            setDrawers({ ...drawers, payCadence: true })
          )
        )
      }
      if (section.sectionType === 'COMMUTER_BENEFIT_SETUP') {
        rows.push(
          CommuterBenefitSetupRow(onboarding, employer.createdAt, () =>
            setDrawers({ ...drawers, commuterBenefitSetup: true })
          )
        )
      }
      if (section.sectionType === 'PROGRAM_LAUNCH') {
        rows.push(
          ProgramLaunchRow(onboarding, employer.createdAt, () =>
            navigate(Locations.Programs.Launch)
          )
        )
      }
      if (section.sectionType === 'EMPLOYEE_INVITE') {
        rows.push(
          EmployeeInviteRow(onboarding, employer.createdAt, () =>
            navigate(Locations.Segments.Overview)
          )
        )
      }
      if (section.sectionType === 'ADDITIONAL_DETAILS') {
        rows.push(
          AdditionalDetailsRow(onboarding, () =>
            setDrawers({ ...drawers, additionalInformation: true })
          )
        )
      }
    })
    return rows.filter((row) => row != null)
  }

  return (
    <>
      <IncompleteOnboardingBanner />
      <Container>
        <OnboardingTable rows={renderRow(onboarding.sections)} />
      </Container>
      <WelcomeModal
        open={!onboarding.welcomeModalShown}
        onClose={() =>
          dispatch(onboardingActions.updateWelcomeModalShown(true))
        }
        type='employer'
      />
      <PayCadenceDrawer
        isOpen={drawers.payCadence}
        onClose={() => setDrawers({ ...drawers, payCadence: false })}
      />
      <KYCCompletionDrawer
        isOpen={drawers.kycCompletion}
        onClose={() => setDrawers({ ...drawers, kycCompletion: false })}
      />
      <WorksiteSyncDrawer
        isOpen={drawers.worksiteSync}
        onClose={() => setDrawers({ ...drawers, worksiteSync: false })}
      />
      <AdditionalInformationDrawer
        isOpen={drawers.additionalInformation}
        onClose={() => setDrawers({ ...drawers, additionalInformation: false })}
      />
      <CommuterBenefitSetupDrawer
        isOpen={drawers.commuterBenefitSetup}
        onClose={() => setDrawers({ ...drawers, commuterBenefitSetup: false })}
      />
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  AsyncItemsView.displayName = 'AsyncItemsView'
}

export default AsyncItemsView
